/**
 * Qui voit quel modèle dans l'aperçu des e-mails (/email-templates-preview).
 *
 * Miroir front de server/constants/email-preview-audience.js — c'est LUI qui
 * fait autorité : le serveur filtre le catalogue et refuse la génération d'un
 * modèle interdit. Cette copie ne sert qu'à l'affichage, pour ne pas proposer
 * à un commercial un lien « Voir le modèle » qui finirait en refus.
 *
 * Les deux listes sont comparées par
 * tests/unit/server/constants/email-preview-audience.test.js : toute
 * modification se fait des deux côtés.
 */
import { hasAdminRights } from './roles.js'

export const COMMERCIAL_TEMPLATE_IDS = [
  // ── Demande de devis — au client ────────────────────────────────
  'confirmation',
  'call_attempt',
  'relance_scheduled',
  'call_attempt_rdv',
  'availability_request',
  'phone_appointment',
  'quote_sent',
  'quote_signed_copy',
  'client_reminder_7days',
  'client_reminder_21days',

  // ── Demande de devis — interne (visibles dans l'historique) ──────
  'quote_new',
  'quote_assigned',
  'quote_unassigned',
  'quote_status_changed',
  'comment_added',

  // ── Inscription — client ────────────────────────────────────────
  'inscription_devis_envoye',
  'inscription_prestataire_refus',
  'inscription_reservation_particulier',
  'inscription_reservation_pro',
  'inscription_confirmation_pro',
  'inscription_stagiaire_convocation',
  'inscription_convocation_donneur_ordre',
  'inscription_confirmation_particulier',
  'inscription_notre_organisme',
  'inscription_session_confirmee',
  'inscription_expiree',
  'inscription_commercial_stagiaires',
  'inscription_commercial_signature',

  // ── Inscription — prestataire ───────────────────────────────────
  'inscription_prestataire_dispo',
  'inscription_prestataire_final',
  'inscription_prestataire_convention',
  'inscription_prestataire_convention_emargement',

  // ── Post-formation ──────────────────────────────────────────────
  'inscription_rib_postformation',
  'inscription_facture_attestations',
  'inscription_attestation_particulier',
  'inscription_post_formation_client',
  'inscription_facture_acquittee',
  'inscription_relance_paiement',

  // ── Relances inscription ────────────────────────────────────────
  'inscription_reminder_prestataire',
  'inscription_reminder_quote_not_signed',
  'inscription_reminder_form_not_filled',
  'inscription_reminder_form_not_finalized',
  'inscription_reminder_stagiaires_not_filled',
  'inscription_reminder_signature_pending',
  'inscription_reminder_convention',
  'inscription_reminder_no_alternative',
  'inscription_reminder_payment',
  'inscription_internal_convention_alert',
  'opco_reminder',
  'liste_stagiaires_reminder',

  // ── Questionnaires ──────────────────────────────────────────────
  'questionnaire_avant',
  'questionnaire_a_chaud',
  'questionnaire_a_froid',
  'questionnaire_demandeur',
  'questionnaire_financeur',
  'questionnaire_organisation_confirmation',
  'questionnaire_commercial_validation',
  'mission_questionnaire_organisation',

  // ── Formateur — atteignables depuis la fiche formateur ───────────
  // `formateur_confirmation` et `formateur_reset_password` en sont absents :
  // ils portent les identifiants du portail (cf. règle 2 ci-dessus).
  'formateur_email_verification',
  'formateur_reprise_inscription',
  'formateur_documents_manquants',
  'formateur_urssaf_a_renouveler',
  'formateur_adresse_modifiee',
  'formateur_invitation_mise_a_jour',
  'formateur_confirmation_rappel_1',
  'formateur_confirmation_rappel_4',
  'formateur_confirmation_rappel_7',

  // ── Missions — atteignables depuis la fiche formateur ────────────
  'mission_proposition',
  'mission_recherche',
  'mission_recherche_relance',
  'mission_recherche_modifiee',
  'mission_confiee_modifiee',
  'mission_confiee_modifiee_client',
  'mission_attribution',
  'mission_non_retenu',
  'mission_retiree',
  'mission_annulee',
  'mission_reprogrammee',
  'mission_urgente',
]

const COMMERCIAL_TEMPLATE_ID_SET = new Set(COMMERCIAL_TEMPLATE_IDS)

/**
 * True si le rôle peut prévisualiser ce modèle d'e-mail.
 * Un administrateur (admin ou super_admin) voit tout le catalogue.
 */
export function canPreviewEmailTemplate(role, templateId) {
  return hasAdminRights(role) || COMMERCIAL_TEMPLATE_ID_SET.has(templateId)
}
